const content = (
  <div>
    <p>
      La cohésion d’une équipe se construit dans le temps, à travers la
      confiance, la qualité des relations et la capacité de chacun à contribuer
      à un objectif commun.
    </p>
    <p>
      Le manager joue un rôle essentiel dans cette dynamique. Il lui revient de
      créer des repères partagés, de favoriser la coopération, de reconnaître
      les contributions de chacun et de développer l’autonomie de ses
      collaborateurs.
    </p>
    <p>
      Cette formation permet d’identifier les leviers qui renforcent le
      collectif et d’acquérir des pratiques concrètes pour mieux mobiliser son
      équipe, faciliter les échanges et installer une dynamique de coopération
      durable.
    </p>

    <h2>Les compétences visées</h2>
    <p>À l’issue de la formation, le participant sera en mesure de :</p>
    <ul>
      <li>Analyser le fonctionnement de son équipe et identifier les facteurs qui influencent sa cohésion.</li>
      <li>Adapter sa posture managériale aux besoins et au niveau d’autonomie de ses collaborateurs.</li>
      <li>Créer un climat de confiance favorisant l’expression, la reconnaissance et la coopération.</li>
      <li>Responsabiliser les collaborateurs à travers une délégation adaptée.</li>
      <li>Favoriser l’entraide, le partage des compétences et la prise de décision collective.</li>
      <li>Fédérer l’équipe autour d’une vision, d’objectifs et de règles de fonctionnement partagés.</li>
      <li>Mettre en place des pratiques permettant d’entretenir la cohésion dans la durée.</li>
    </ul>

    <h2>Public concerné</h2>
    <p>Managers, responsables d’équipe, chefs de projet et toute personne amenée à coordonner ou encadrer une équipe.</p>
    <h2>Prérequis</h2>
    <p>Aucun prérequis spécifique n’est nécessaire.</p>
    <h2>Méthodes et moyens pédagogiques</h2>
    <p>Apports méthodologiques, échanges d’expériences, autodiagnostics, études de cas, exercices individuels et collectifs, jeux de rôles et mises en situation.</p>
    <p>La formation s’appuie sur les situations professionnelles des participants afin de leur permettre d’identifier leurs propres leviers de cohésion et de tester des pratiques directement transposables dans leur quotidien. Les exercices et mises en situation donnent lieu à des débriefings individuels et collectifs.</p>
    <h2>Modalités d’évaluation</h2>
    <p>Le formateur évalue la progression pédagogique du participant tout au long de la formation au moyen de questionnaires, exercices, études de cas et mises en situation.</p>
    <p>Un positionnement est également réalisé en amont et en fin de formation afin de mesurer la progression du participant et de valider l’acquisition des compétences visées.</p>
    <h2>Modalités d’accès</h2>
    <p>Inscription auprès de Natelco - Les Ateliers de la Connaissance par téléphone ou par e-mail.</p>
    <p>Un échange préalable permet d’identifier les besoins du participant et de vérifier l’adéquation de la formation avec ses attentes et ses objectifs professionnels.</p>
    <h2>Délai d’accès</h2>
    <p>L’inscription est possible jusqu’à 7 jours ouvrés avant le début de la formation, sous réserve de disponibilité.</p>
    <p>Pour les formations organisées sur mesure, les dates sont définies en concertation avec le commanditaire en fonction de ses besoins et des disponibilités.</p>
    <h2>Accessibilité</h2>
    <p>Les personnes en situation de handicap sont invitées à nous contacter afin d’identifier leurs besoins et d’étudier les adaptations pédagogiques, matérielles ou organisationnelles pouvant être mises en place.</p>

    <h2>Programme de la formation</h2>
    <h2>1/ Comprendre ce qui construit la cohésion d’une équipe</h2>
    <ul>
      <li>Identifier les éléments qui permettent à un groupe de devenir une véritable équipe.</li>
      <li>Repérer les différentes étapes d’évolution d’un collectif de travail.</li>
      <li>Clarifier les attentes, les besoins et les rôles de chacun.</li>
      <li>Identifier les compétences et les complémentarités présentes dans l’équipe.</li>
      <li>Observer les niveaux d’engagement, de motivation et d’autonomie des collaborateurs.</li>
      <li>Prendre du recul sur son propre mode de management et sur ses effets sur le collectif.</li>
      <li>Comprendre l’influence des émotions et des réactions individuelles sur la dynamique du groupe.</li>
    </ul>
    <h3>Travaux pratiques</h3>
    <p>Diagnostic de son équipe et identification des principaux facteurs qui renforcent ou fragilisent sa cohésion.</p>

    <h2>2/ Adopter une posture managériale qui renforce la confiance</h2>
    <ul>
      <li>Développer une communication claire et assertive.</li>
      <li>Créer un environnement dans lequel chacun peut s’exprimer et trouver sa place.</li>
      <li>Prendre en compte les besoins de reconnaissance et de considération des collaborateurs.</li>
      <li>Valoriser les réussites, les efforts et les contributions.</li>
      <li>Formuler des signes de reconnaissance adaptés aux situations.</li>
      <li>Adapter son accompagnement au niveau de compétence et d’autonomie de chacun.</li>
      <li>Déléguer de manière progressive et responsabilisante.</li>
      <li>Donner un cadre clair tout en laissant une marge d’initiative.</li>
    </ul>
    <h3>Travaux pratiques</h3>
    <p>Jeux de rôles autour de la reconnaissance, de la responsabilisation et de la délégation.</p>

    <h2>3/ Développer la coopération au quotidien</h2>
    <ul>
      <li>Favoriser l’écoute et le respect mutuel dans les échanges.</li>
      <li>Identifier les intérêts communs au-delà des différences individuelles.</li>
      <li>Encourager l’entraide et le partage des compétences.</li>
      <li>Créer des occasions de travailler de manière transversale.</li>
      <li>Développer l’autonomie sans perdre la dynamique collective.</li>
      <li>Associer les collaborateurs à la recherche de solutions.</li>
      <li>Faciliter la prise de décision en équipe lorsque la situation le permet.</li>
      <li>Accompagner le développement des compétences au sein du collectif.</li>
      <li>Prévenir les fonctionnements trop individualisés ou les phénomènes de cloisonnement.</li>
    </ul>
    <h3>Travaux pratiques</h3>
    <p>Étude de cas autour d’une problématique collective nécessitant coopération, prise de décision et mobilisation des compétences de chacun.</p>

    <h2>4/ Installer des repères pour faire durer la cohésion</h2>
    <ul>
      <li>Définir une vision commune et la traduire en priorités compréhensibles par tous.</li>
      <li>Partager des objectifs collectifs et donner du sens aux actions menées.</li>
      <li>Clarifier les règles de fonctionnement de l’équipe.</li>
      <li>Mettre en place des temps d’échange permettant de réguler le fonctionnement collectif.</li>
      <li>Exprimer un désaccord ou une difficulté de manière constructive.</li>
      <li>Utiliser une communication structurée pour traiter les situations sensibles.</li>
      <li>Développer la souplesse et la capacité d’adaptation de l’équipe.</li>
      <li>Donner des repères dans le temps et rendre visibles les progrès réalisés.</li>
      <li>Résoudre les problèmes en associant les collaborateurs à la recherche de solutions.</li>
      <li>Valoriser les réussites collectives et entretenir le sentiment d’appartenance.</li>
    </ul>
    <h3>Travaux pratiques</h3>
    <p>Construction d’un plan d’action destiné à renforcer durablement la cohésion de son équipe à partir des situations professionnelles du participant.</p>

    <h2>Durée</h2>
    <p>Deux jours de 9h00 à 17h00, soit 7 heures effectives de formation par jour.</p>
    <h2>Modalités d’organisation</h2>
    <p>Formation réalisable en présentiel, à distance ou en format hybride.</p>
    <p>En intra-entreprise : chez le client, à distance ou en format hybride.</p>
    <p>En inter-entreprises : à distance ou dans une salle de formation proposée à Paris.</p>
    <p>Taille du groupe : jusqu’à 10 participants.</p>
    <p>Dates : définies selon le planning et les modalités convenues avec le client.</p>
    <h2>Tarifs</h2>
    <p>Inter-entreprises : 1 200 € HT par participant pour les 2 jours de formation.</p>
    <p>Intra-entreprise : tarif sur devis, établi selon les besoins, les modalités et le nombre de participants.</p>
  </div>
);

export default content;
